import * as PortalAction from "@src/Actions/PortalActions"
import Tooltip from "@src/Components/Tooltip"
import C from "@src/Controller"
import { VIEW_PATHS } from "@src/Routes/constants"
import * as T from "@src/types"
import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"

import { tState } from "../../Model/Model"
import Menu from "../Menu"
import { MenuBackDrop, SubscribeLink } from "./styles"

export interface IAvatarData {
  username: string
  orgName: string
  hasGradientBackground: boolean
  menuItems: {
    text: string
    iconName: "sign-out"
    onClick: () => void
    hasDivider?: boolean
  }[]
}

interface IMenuItem {
  value: string
  label: string
  icon: string
  isFaIcon: boolean
  iconInactive: string
  isExternal?: boolean
  isDisabled?: boolean
  onSelect?: () => void
}

export interface IGroupItem {
  groupHeader?: string
  value?: string
  hasGroupSeparator?: boolean
  groupItems: IMenuItem[]
}

interface IMenuContainer {
  hidden: boolean
  width: number
  offset?: number
  isOpen: boolean
  uiRules?: T.tChannelUiRules
  onClose: () => void
}

const MenuContainer: React.FC<IMenuContainer> = ({
  hidden,
  width,
  offset,
  isOpen,
  uiRules,
  onClose,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const pathname = `/${history.location.pathname.split("/")[1]}`
  const isSubscriptionPage = pathname == VIEW_PATHS.subscription
  const portalMenu = useSelector((state: tState) => state.portal.portalMenu)
  const modules = useSelector((state: tState) => state.plans.modules)
  const helpCenter = useSelector((state: tState) => state.brand.helpCenter)
  const companyProfile = useSelector(
    (store: tState) => store.portal.companyProfile,
  )
  const userProfile = useSelector((store: tState) => store.portal.userProfile)
  const email = userProfile?.email || ""
  const username = [userProfile?.firstName, userProfile?.lastName]
    .filter((name) => name)
    .join(" ")

  const avatarData: IAvatarData = {
    username: username || email,
    orgName: companyProfile?.companyName,
    hasGradientBackground: true,
    menuItems: [
      {
        text: "Sign Out",
        iconName: "sign-out",
        onClick: () => C.Portal.logout(),
      },
    ],
  }

  const menuItems: IGroupItem[] = [
    {
      hasGroupSeparator: true,
      groupItems: [
        {
          value: "dashboard",
          label: "Dashboard",
          icon: "table-columns",
          isFaIcon: true,
          iconInactive: "",
        },
        {
          value: "cyberconcierge",
          label: "Cyber Concierge",
          icon: "bell-concierge",
          isFaIcon: true,
          iconInactive: "",
        },
      ],
    },
    {
      groupHeader: "Security Foundations",
      value: "securityFoundations",
      hasGroupSeparator: false,
      groupItems: [
        {
          value: "cybermaturity",
          label: "Cyber Maturity",
          icon: "shield",
          isFaIcon: true,
          iconInactive: "",
        },
        {
          value: "compliance",
          label: "Security Policies",
          isFaIcon: true,
          icon: "list-check",
          iconInactive: "",
        },
      ],
    },
    {
      value: "teamReadiness",
      groupHeader: "Team Readiness",
      hasGroupSeparator: false,
      groupItems: [
        {
          value: "training",
          label: "Training",
          icon: "graduation-cap",
          isFaIcon: true,
          iconInactive: "",
        },
      ],
    },
    {
      value: "exposureMonitoring",
      groupHeader: "Exposure Monitoring",
      hasGroupSeparator: true,
      groupItems: [
        {
          value: "monitoring",
          label: "Website Monitoring",
          isFaIcon: true,
          icon: "radar",
          iconInactive: "",
        },
      ],
    },
    {
      hasGroupSeparator: false,
      groupItems: [
        {
          value: "marketplace",
          label: "Marketplace",
          icon: "handshake",
          isFaIcon: true,
          iconInactive: "",
        },
        {
          value: "knowledgeBase",
          label: "Knowledge Base",
          isFaIcon: true,
          icon: "book-open",
          iconInactive: "",
          isExternal: true,
          onSelect: () => window.open(`https://${helpCenter}`),
        },
        {
          value: "settings",
          label: "Settings",
          icon: "cog",
          isFaIcon: true,
          iconInactive: "",
        },
      ],
    },
  ]

  const setView = (view: string) => {
    onClose()
    dispatch(PortalAction.setPortalMenu(view))
    history.push(VIEW_PATHS[view])
  }

  const availableMenuItems = menuItems.map((group) => {
    const groupItems = group.groupItems.reduce<IMenuItem[]>((acc, cur) => {
      if (modules[cur.value] === "NOT_AVAILABLE") {
        return acc
      }
      const item = {
        ...cur,
        isDisabled: modules[cur.value] === "INACTIVE" ? true : undefined,
      }
      acc = [...acc, item]
      return acc
    }, [])
    return { ...group, groupItems }
  })

  // The menu's own tooltip renders inside SideNav, which clips it away
  // (`overflow-x: hidden` on the scroll container, `overflow: hidden` on the
  // collapsible group). Render our own tooltips outside that subtree instead,
  // anchored to the id MenuItem puts on every row: `tooltip-<value>`.
  const disabledMenuItemValues = availableMenuItems.flatMap((group) =>
    group.groupItems
      .filter((item) => item.isDisabled)
      .map((item) => item.value),
  )

  // On mobile (`width === 0`) the nav is an overlay whose anchors stay mounted
  // when it closes, so a tooltip opened by tap would linger next to the
  // off-screen anchor. Unmount the tooltips with the overlay.
  const isNavVisible = !!width || isOpen

  React.useEffect(() => {
    if (pathname === "/attacksurface") {
      dispatch(PortalAction.setPortalMenu("monitoring"))
    }
  }, [portalMenu])

  if (hidden) {
    return null
  }

  return (
    <>
      <Menu
        avatarData={avatarData}
        menuItems={availableMenuItems}
        width={width}
        offset={offset}
        hidden={hidden}
        isOpen={isOpen}
        logo={uiRules?.marketingLogoPrimary}
        setView={setView}
      />
      {isNavVisible &&
        disabledMenuItemValues.map((value) => (
          <Tooltip
            key={value}
            id={`menu-subscribe-${value}`}
            anchorSelect={`#tooltip-${value}`}
            place="right"
            clickable
            content={
              <SubscribeLink
                disabled={isSubscriptionPage}
                onClick={() => setView("subscription")}
              >
                Subscribe to use
              </SubscribeLink>
            }
          />
        ))}
      {!width && isOpen && <MenuBackDrop onClick={onClose} />}
    </>
  )
}

export default MenuContainer
